// deps
import * as React from "react";

// internal-deps
import {
    PaymentMethod,
    PaymentMethodMeta,
    PaymentProgressLoader,
    WalletPaymentMethodData,
} from "@swiggy-private/payment-ui";
import { CheckoutContext, CheckoutActionTypes } from "@swiggy-private/checkout-core";
import { Some, Option, None } from "@swiggy-private/js-fp";
import { OnPayExtraParams, WalletApiFetchState } from "@swiggy-private/payment-core";

// hooks
import { useWalletBalanceData } from "@payment-client/hooks/UseWalletBalanceData";

// contexts
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";

// helpers
import { findWalletPaymentMethod } from "@payment-client/helpers/WalletHelper";
import { removeAddMoneySelectedWallet, getAddMoneySelectedWallet } from "@payment-client/helpers/CheckoutStateHelper";

export interface AddMoneyConfirmProps {
    payAction: (paymentMethodData: PaymentMethod<PaymentMethodMeta>, _extraParams?: OnPayExtraParams) => void;
    paymentAmount: number;
}

export function AddMoneyConfirm({ paymentAmount, payAction }: AddMoneyConfirmProps): React.ReactElement | null {
    const { setCheckoutState } = React.useContext(CheckoutContext);
    const { paymentResponse } = React.useContext(ShowPaymentContext);
    const walletBalanceData = useWalletBalanceData({ paymentAmount });
    const [walletCode] = React.useState<string>(() => getAddMoneySelectedWallet() || "");
    const walletData = React.useMemo(() => Some(walletBalanceData[walletCode]), [walletBalanceData, walletCode]);
    const paymentMethod = React.useMemo<Option<WalletPaymentMethodData>>(
        () => (paymentResponse ? findWalletPaymentMethod(paymentResponse.paymentGroup, walletCode) : None),
        [paymentResponse, walletCode],
    );

    React.useEffect(() => {
        removeAddMoneySelectedWallet();
        if (walletData.isNone() || paymentMethod.isNone()) {
            setCheckoutState({ type: CheckoutActionTypes.SHOW_PAYMENTS });
            return;
        }

        const { fetchState, data, refetch } = walletData.unwrap();

        if (fetchState == WalletApiFetchState.DEFAULT) {
            refetch();
        }

        if (fetchState === WalletApiFetchState.ERROR) {
            setCheckoutState({ type: CheckoutActionTypes.SHOW_PAYMENTS });
            return;
        }

        if (fetchState === WalletApiFetchState.SUCCESS) {
            setCheckoutState({ type: CheckoutActionTypes.SHOW_PAYMENTS });
            if (data.isWalletBalanceSufficent) {
                payAction(paymentMethod.unwrap());
            }

            return;
        }
    }, [setCheckoutState, walletData, payAction, paymentMethod]);

    if (walletData.isNone() || paymentMethod.isNone()) {
        return null;
    }

    return <PaymentProgressLoader />;
}
